import type {
  CreateKanbanBoardItemPayload,
  CreateKanbanColumnPayload,
  DeleteKanbanBoardItemPayload,
  DeleteKanbanColumnPayload,
  KanbanContext,
  KanbanWorkitemMutationPayload,
  UpdateKanbanBoardItemPayload,
  UpdateKanbanColumnPayload,
} from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import type { Ref } from 'vue'
import { toValue, unref } from 'vue'
import type { HarmonyItem } from '~/src/queries'
import handleAddExistingItem from './useKanbanBoardContext.addExistingItem'
import handleCreateBoardItem from './useKanbanBoardContext.createBoardItem'
import handleCreateColumn from './useKanbanBoardContext.createColumn'
import handleDeleteBoardItem from './useKanbanBoardContext.deleteBoardItem'
import handleDeleteColumn from './useKanbanBoardContext.deleteColumn'
import handleUpdateBoard from './useKanbanBoardContext.updateBoard'
import handleUpdateBoardItem from './useKanbanBoardContext.updateBoardItem'
import handleUpdateColumn from './useKanbanBoardContext.updateColumn'

import { handleUpdateWorkitem } from './useKanbanBoardContext.updateWorkitem'

type CollectionProvider = () =>
  | {
      boardItems?: Collection<any>
      workItems?: Collection<any>
      teamTags?: Collection<any>
      workitemTags?: Collection<any>
    }
  | undefined

export default function createMutationHandlers(
  context: Ref<KanbanContext | undefined>,
  collections?: CollectionProvider,
) {
  return {
    createColumn: (payload: CreateKanbanColumnPayload): Transaction => handleCreateColumn(payload, unref(context)),
    updateColumn: (payload: UpdateKanbanColumnPayload): Transaction => handleUpdateColumn(payload, unref(context)),
    deleteColumn: (payload: DeleteKanbanColumnPayload): Transaction => handleDeleteColumn(payload, unref(context)),
    createBoardItem: (payload: CreateKanbanBoardItemPayload): Transaction =>
      handleCreateBoardItem(payload, unref(context)),
    updateBoardItem: (payload: UpdateKanbanBoardItemPayload): Transaction =>
      handleUpdateBoardItem(payload, unref(context), collections?.()),
    deleteBoardItem: (payload: DeleteKanbanBoardItemPayload): Transaction =>
      handleDeleteBoardItem(payload, unref(context)),
    addExistingItem: (payload: HarmonyItem[]): Transaction<HarmonyItem> =>
      handleAddExistingItem(payload, unref(context)),
    updateBoard: (payload: any): Transaction => {
      if (!unref(context)) throw new Error('Kanban board context is not ready: missing context for updating a board')
      return handleUpdateBoard(toValue(context)!.boardId, payload, toValue(context)!)
    },
    updateWorkitem: (payload: KanbanWorkitemMutationPayload): Transaction => {
      const resolvedCollections = collections?.()
      return handleUpdateWorkitem(payload, unref(context), {
        workitemsCollection: resolvedCollections?.workItems,
        teamTagsCollection: resolvedCollections?.teamTags,
        workitemTagsCollection: resolvedCollections?.workitemTags,
      })
    },
  }
}
